/// <amd-module name="Search/WorkItem/Flux/ActionCreator" />
import * as Constants from "Search/WorkItem/Constants";
import { ActionsHub } from "Search/WorkItem/Flux/ActionsHub";
import { deserialize, stripOffRelevance } from "Search/Shared/Utils";
import { EntitySortOption } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { Filter, FILTER_CHANGE_EVENT, FILTER_RESET_EVENT, IFilterState } from "SearchUI/Utilities/Filter";
import { getFieldValue } from "Search/WorkItem/Utils";
import { IAggregatedState } from "Search/WorkItem/Flux/StoresHub";
import { IPreviewSetting, ISortOption } from "Search/Shared/Base/Types";
import { ISeedDataSearchService, ISearchServiceState, SearchServiceEventNames } from "Search/Coordinator/SeedDataSearchService";
import { ISearchFailedPayload } from "Search/Shared/Base/ActionsHub";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssPageContext } from "VSS/Platform/Context";
import { IWorkItemDialogService } from "Search/WorkItem/Services/WorkItemDialogService";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { scrubFilters } from "Search/WorkItem/Flux/Stores/FilterStore";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { SearchSourceType } from "Search/Shared/Base/Sources/SearchSource";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";
import { TelemetryWriter } from "Search/WorkItem/Flux/Sources/TelemetryWriter";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { WorkitemPreviewPaneScenario } from "Search/WorkItem/Flux/Stores/PreviewNotificationStore";
import { WorkItemResult, WorkItemSearchRequest, WorkItemSearchResponse, WorkItemFieldType } from "Search/LegacyWebApi/Contracts/WorkItem";
import { WorkItemSearchSource } from "Search/WorkItem/Flux/Sources/WorkItemSearchSource";

export interface ISources {
    searchSource: WorkItemSearchSource;

    pageSource: PageSource;
}

export class ActionCreator {
    private readonly timerManagement: TimerManagement;
    private readonly historyService: IVssHistoryService;
    private readonly seedDataSearchService: ISeedDataSearchService;

    constructor(
        private readonly pageContext: IVssPageContext,
        private readonly actionsHub: ActionsHub,
        private readonly sources: ISources,
        private readonly getAggregatedState: () => IAggregatedState,
        private readonly filter: Filter,
        private readonly telemetryWriter: TelemetryWriter,
        private readonly includeFacets: boolean,
        private readonly useSeedDataSearchService: boolean
    ) {
        this.timerManagement = new TimerManagement();
        this.filter.subscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.subscribe(this.resetFilters, FILTER_RESET_EVENT);
        this.seedDataSearchService = this.pageContext.getService<ISeedDataSearchService>("ISeedDataSearchService");

        if (this.useSeedDataSearchService) {
            this.seedDataSearchService.subscribe(this.updateStoresWithSeedData, SearchServiceEventNames.SearchEntityResultsFetched)
        }
        this.historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    public dispose = (): void => {
        this.filter.unsubscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.unsubscribe(this.resetFilters, FILTER_RESET_EVENT);
        this.seedDataSearchService.unsubscribe(this.updateStoresWithSeedData, SearchServiceEventNames.SearchEntityResultsFetched);
        this.timerManagement.dispose();
    }

    public loadInitialState = (
        searchText: string,
        includeFacets: boolean,
        filters?: string,
        sortOptions?: string,
        activeTab?: string,
        launchPoint?: string,
        takeResults?: string): void => {
        const { searchStoreState } = this.getAggregatedState();
        // This action is only used for logging telemetry and pivot count (when response is already in the page)
        this.actionsHub.pageInitializedWithSeedData.invoke({ activeTabKey: activeTab, launchPoint, response: searchStoreState.response });
        if (!searchStoreState.response) {
            const deserializedSortOptions = deserialize<EntitySortOption[]>(sortOptions) || [];
            const request: WorkItemSearchRequest = {
                searchText,
                skipResults: 0,
                takeResults: takeResults && !isNaN(parseInt(takeResults)) ? parseInt(takeResults) : Constants.WorkItemSearchTakeResults,
                sortOptions: stripOffRelevance(deserializedSortOptions, Constants.SortActionIds.Relevance),
                summarizedHitCountsNeeded: includeFacets,
                searchFilters: deserialize<{ [key: string]: string[] }>(filters) || {},
                filters: [],
                includeSuggestions: false
            };

            this.actionsHub.pageInitializationStarted.invoke({ request });

            this.sources
                .searchSource
                .getQueryResults(request, ({ responseWithActivityId, source }) => {
                    if (responseWithActivityId) {
                        const workItemSearchResponse = responseWithActivityId.response as WorkItemSearchResponse;
                        const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];
                        this.handleResultsLoaded(request, workItemSearchResponse, source, activityId);
                        this.timerManagement.setTimeout(() => {
                            if (!includeFacets) {
                                this.fetchFacets(request);
                            }
                        }, 0);
                    }
                }, (error: ISearchFailedPayload<WorkItemSearchRequest>) => {
                    this.actionsHub.searchFailed.invoke({ ...error, query: request });
                });
        }
        else if (!includeFacets) {
            this.fetchFacets(searchStoreState.response.query);
        }
    }

    public loadInitialStateWithSeedData = (
        searchText: string,
        includeFacets: boolean,
        filters?: string,
        sortOptions?: string,
        activeTab?: string,
        launchPoint?: string,
        takeResults?: string): void => {
        const { searchStoreState } = this.getAggregatedState();
        // This action is only used for logging telemetry and pivot count (when response is already in the page)
        this.actionsHub.pageInitializedWithSeedData.invoke({ activeTabKey: activeTab, launchPoint, response: searchStoreState.response });

        if (!searchStoreState.response) {
            const deserializedSortOptions = deserialize<EntitySortOption[]>(sortOptions) || [];
            const request: WorkItemSearchRequest = {
                searchText,
                skipResults: 0,
                takeResults: takeResults && !isNaN(parseInt(takeResults)) ? parseInt(takeResults) : Constants.WorkItemSearchTakeResults,
                sortOptions: stripOffRelevance(deserializedSortOptions, Constants.SortActionIds.Relevance),
                summarizedHitCountsNeeded: includeFacets,
                searchFilters: deserialize<{ [key: string]: string[] }>(filters) || {},
                filters: [],
                includeSuggestions: false
            };

            this.actionsHub.pageInitializationStarted.invoke({ request });
            const seedData = this.seedDataSearchService.getSearchServiceState();

            if (!(seedData.searchInProgress && seedData.searchText === searchText)) {
                this.sources
                    .searchSource
                    .getQueryResults(request, ({ responseWithActivityId, source }) => {
                        if (responseWithActivityId) {
                            const workItemSearchResponse = responseWithActivityId.response as WorkItemSearchResponse;
                            const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];
                            this.handleResultsLoaded(request, workItemSearchResponse, source, activityId);
                            this.timerManagement.setTimeout(() => {
                                if (!includeFacets) {
                                    this.fetchFacets(request);
                                }
                            }, 0);
                        }
                    }, (error: ISearchFailedPayload<WorkItemSearchRequest>) => {
                        this.actionsHub.searchFailed.invoke({ ...error, query: request });
                    });
            }
        }
        else {
            this.notifyResultsFetchedFromSeedDataService(true);

            if (!includeFacets) {
                this.fetchFacets(searchStoreState.response.query);
            }
        }
    }

    public performSearch = (
        text: string,
        sortOptions: EntitySortOption[],
        filters: { [id: string]: string[] }): void => {
        const searchQuery = {
            searchText: text,
            skipResults: 0,
            summarizedHitCountsNeeded: this.includeFacets,
            takeResults: this.getInitialTake(),
            sortOptions,
            searchFilters: filters,
            filters: [],
            includeSuggestions: false
        } as WorkItemSearchRequest;

        this.doSearch(searchQuery);
    }

    /**
     *  Send query on landing page scenario to update searchstore to an adequate state
     */
    public showLandingPage = (filters?: string): void => {
        const request: WorkItemSearchRequest = {
            skipResults: 0,
            takeResults: this.getInitialTake(),
            sortOptions: [],
            summarizedHitCountsNeeded: true,
            searchFilters: deserialize<{ [key: string]: string[] }>(filters) || {},
            filters: [],
            includeSuggestions: false,
            searchText: ""
        };

        this.actionsHub.pageInitializationStarted.invoke({ request, isLandingPage: true });
    }

    public applySearchText = (searchText: string) => {
        const { searchStoreState } = this.getAggregatedState();
        const searchRequest: WorkItemSearchRequest = {
            ...searchStoreState.query,
            searchText,
            skipResults: 0,
            takeResults: this.getInitialTake(),
            summarizedHitCountsNeeded: this.includeFacets
        };

        this.doSearch(searchRequest, false, true);
    }

    public applyFilter = (filterState: IFilterState): void => {
        const currentFilterState = this.filter.getState();
        const aggregatedState = this.getAggregatedState();
        const { filterStoreState, appliedEntitySortOption, searchStoreState } = aggregatedState;
        const { filterItems } = filterStoreState;
        let filters: { [key: string]: string[] } = {};
        Object
            .keys(currentFilterState)
            .forEach((key) => {
                const value = currentFilterState[key].value;
                if (!value) {
                    return;
                }

                if (filterItems[key].enabled) {
                    filters[key] = value;
                }
            });

        // Create a new search query with updated filters and latest applied sort options.
        const searchQuery = {
            ...searchStoreState.query,
            searchFilters: scrubFilters(filters),
            sortOptions: [appliedEntitySortOption],
            skipResults: 0,
            takeResults: this.getInitialTake(),
            summarizedHitCountsNeeded: this.includeFacets
        };

        this.doSearch(searchQuery, true);
    }

    public resetFilters = (): void => {
        this.actionsHub.filterReset.invoke(undefined);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FilterResetClicked);
    }

    public onFiltersChanged = (name: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FiltersUpdated, { name });
    }

    public toggleFilterPane = () => {
        this.actionsHub.filterPaneToggled.invoke(undefined);
    }

    public openSearchInNewTab = (searchText: string): void => {
        const { searchFilters, sortOptions } = this.getAggregatedState().searchStoreState.query;
        this.sources.pageSource.navigateToNewSearch(Constants.EntityTypeUrlParam, searchText, searchFilters, sortOptions);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.SearchInNewTab);
    }

    public fetchMoreResults = (newSkipResults: number, newTakeResults: number) => {
        const { searchStoreState } = this.getAggregatedState();

        if (searchStoreState.searchStatus !== SearchStatus.Loading) {
            const searchQuery = {
                ...searchStoreState.query,
                skipResults: newSkipResults,
                takeResults: newTakeResults,
                summarizedHitCountsNeeded: this.includeFacets
            };

            this.doSearch(searchQuery, false, false, true);
        }
    }

    public changeActiveItem = (item: WorkItemResult, activatedOnPreviousNavigation?: boolean) => {
        const { selectedItem } = this.getAggregatedState();
        if (item && item !== selectedItem) {
            this.actionsHub.itemChanged.invoke({ item, activatedOnPreviousNavigation });
        }
    }

    public publishNotificationBanner = (scenarioType: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.NotificationBanner, { scenarioType });
    }

    public errorNotificationBannerDismissed = (): void => {
        this.actionsHub.errorNotificationBannerDismissed.invoke({});
    }

    public changePreviewOrienation = (previewOrientation: IPreviewSetting) => {
        this.actionsHub.previewOrientationChanged.invoke({ previewOrientation: previewOrientation });
    }

    public changeSortCriteria = (sortOption: ISortOption) => {
        const { searchStoreState } = this.getAggregatedState();
        const { response, query } = searchStoreState;
        const entitySortOption: EntitySortOption = { field: sortOption.key, sortOrder: sortOption.order };

        if (this.shouldPerformServerSort(response, sortOption.key)) {
            const entitySortOptions: EntitySortOption[] =
                sortOption.key !== Constants.SortActionIds.Relevance
                    ? [{
                        field: sortOption.key,
                        sortOrder: sortOption.order
                    }]
                    : [],
                searchQuery: WorkItemSearchRequest = {
                    ...query,
                    skipResults: 0,
                    takeResults: this.getInitialTake(),
                    sortOptions: entitySortOptions,
                    summarizedHitCountsNeeded: this.includeFacets
                };
            this.doSearch(searchQuery);
        } else {
            const items = response ? response.results.values : [];
            this.sortItems(items, entitySortOption);
            this.actionsHub.sortOptionChanged.invoke({ sortedItems: items, sortOption: entitySortOption });
        }
    }

    public clickAccountButton = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        this.sources.pageSource.openCollectionSearch(Constants.EntityTypeUrlParam, searchStoreState.query.searchText, this.includeFacets);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.AccountButtonClicked);
    }

    public clickFeedbackLink = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FeedbackLinkClicked);
    }

    public notifyResultsRendered = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        const data = {
            responseActivityId: searchStoreState.activityId,
            searchSource: SearchSourceType[searchStoreState.source]
        };

        this.telemetryWriter.notifyResultsRendered(data);
    }

    public showBanner = (scenario: WorkitemPreviewPaneScenario) => {
        this.actionsHub.showPreviewMessageBanner.invoke(scenario);
    }

    public dismissBanner = () => {
        setTimeout(() => this.actionsHub.dismissPreviewMessageBanner.invoke({}), 0);
    }

    public notifySearchFailed = (): void => {
        this.telemetryWriter.notifySearchFailed();
    }

    public notifyFeedbackMailLinkClicked = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FeedbackMailLinkClicked);
    }

    public onPreviewLoaded = (): void => {
        this.telemetryWriter.notifyPreviewRendered(true);
    }

    public onPreviewLoadFailed = (): void => {
        this.telemetryWriter.notifyPreviewRendered(false);
    }

    public publishZeroData = (scenarioType: string, error?: any): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.ZeroData, { scenarioType, error });
    }

    public onWorkItemInvoked = (workItemId: number, index: number, url: string, openInNewTab: boolean): void => {
        if (openInNewTab) {
            this.openWorkItemInNewTab(url, workItemId, index);
        } else {
            this.openWorkItemInModalDialog(workItemId, index);
        }
    }

    public changeSplitterFixedSize = (newFixedSize: number): void => {
        this.actionsHub.splitterFixedSizeChanged.invoke(newFixedSize);
    }

    private notifyResultsFetchedFromSeedDataService = (isSeedData: boolean): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.ResultsFetchedFromSeedDataService, { isSeedData });
    }

    private updateStoresWithSeedData = (seedData: ISearchServiceState) => {
        const { searchStoreState } = this.getAggregatedState();
        const routeParams: { [key: string]: string } = this.historyService.getState();

        // We use the data from the SeedDataSearchService only if the search store is not yet initialized
        if (seedData.searchText === routeParams.text) {
            if (!searchStoreState.response && seedData.responseWithActivityId) {
                const response = seedData.responseWithActivityId.response as WorkItemSearchResponse;
                const items = response && response.results && response.results.values;
                const activityId = seedData.responseWithActivityId.activityId && seedData.responseWithActivityId.activityId[0];
                if (response) {
                    this.initializeSearchPage(searchStoreState.query, response.query);
                    this.timerManagement.setTimeout(() => {
                        this.handleResultsLoaded(response.query, response, SearchSourceType.RestApi, activityId, items && items[0]);
                    }, 0);
                    this.timerManagement.setTimeout(() => {
                        if (!this.includeFacets) {
                            this.fetchFacets(response.query);
                        }
                    }, 0);
                }
            } else if (!searchStoreState.response && !searchStoreState.error && seedData.error) {
                this.initializeSearchPage(searchStoreState.query, seedData.error.query);
                this.timerManagement.setTimeout(() => {
                    const error = seedData.error && seedData.error.error;
                    const activityId = seedData.error && seedData.error.activityId;
                    const query = seedData.error && seedData.error.query;

                    if (error && query && activityId) {
                        this.actionsHub.searchFailed.invoke({ error, activityId, query });
                    }
                }, 0);
            }

            this.notifyResultsFetchedFromSeedDataService(false);
        }
    }

    private fetchFacets = (request: WorkItemSearchRequest): void => {
        const requestWithIncludeFacets = { ...request, summarizedHitCountsNeeded: true, takeResults: 1 };
        this.sources
            .searchSource
            .getFacets(requestWithIncludeFacets, (responseWithActivityId) => {
                if (responseWithActivityId) {
                    const workItemSearchResponse = responseWithActivityId.response as WorkItemSearchResponse;
                    this.actionsHub.facetsLoaded.invoke({ response: workItemSearchResponse });
                }
            }, () => {
                this.actionsHub.fetchfacetsFailed.invoke({ query: request });
            });
    }

    private openWorkItemInModalDialog = (workItemId: number, index: number): void => {
        const workItemDialogService = this.pageContext.getService<IWorkItemDialogService>("IWorkItemDialogService");
        workItemDialogService.openWorkItem(workItemId);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.WorkItemOpenForm, 
            { 
                originArea: "WITSearch",
                IsDialog: true ,
                searchId: this.getAggregatedState().searchStoreState.activityId,
                WitId: workItemId,
                index: index
            });
    }

    private openWorkItemInNewTab = (url: string, workItemId: number, index: number): void => {
        this.sources.pageSource.openUrlInNewtab(url);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.WorkItemOpenForm, 
            { 
                originArea: "WITSearch",
                IsDialog: false,
                searchId: this.getAggregatedState().searchStoreState.activityId,
                WitId: workItemId,
                index: index
            })
    }

    private shouldPerformServerSort(response: WorkItemSearchResponse | undefined, sortOptionKey: string): boolean {
        // Perform server sort even if the previous response was null(coulbe a error scenario)
        return !response ||
            response.results.count > response.results.values.length ||
            sortOptionKey === Constants.SortActionIds.Relevance;
    }

    private sortItems = (items: WorkItemResult[], sortOption: EntitySortOption): void => {
        items.sort(this.comparer(sortOption));
    }

    private handleResultsLoaded = (
        searchQuery: WorkItemSearchRequest,
        response: WorkItemSearchResponse,
        source: SearchSourceType,
        activityId: string,
        activeItem?: WorkItemResult): void => {
        const items = response && response.results && response.results.values;
        // Sort the tags in the work item results based on the current browser locale.
        if(isFeatureFlagEnabled(this.pageContext, Constants.FeatureFlagConstants.EnableTagsSortingForWorkitem, false))
        {
            items.forEach((item: WorkItemResult) => {
                if (item.fields) {
                    item.fields.forEach(field => {
                        if (field.name === "Tags" && field.referenceName === "system.tags" && field.value) {
                            var arr: string[] = field.value.split(";");
                            for (var i = 0; i < arr.length; i++) {
                                arr[i] = arr[i].trim();
                            }                      
                            arr.sort((a, b) => a.localeCompare(b, navigator.language, { sensitivity: 'base' }));
                            field.value = arr.join(";");
                            arr = []; // Deallocate array
                        }
                    });
                }
            });
        }
        // If no item is activated by default, first item or the item at index "activeItemIndex" in the list of sorted results set is the one which needs to be activated.
        activeItem = activeItem || items[0];

        this.actionsHub.resultsLoaded.invoke({ response, activeItem, activityId, source });
    }

    private doSearch(
        request: WorkItemSearchRequest,
        filterApplication?: boolean,
        searchTextModified?: boolean,
        fetchMoreScenario?: boolean
    ): void {
        this.actionsHub.searchStarted.invoke({ request, fetchMoreScenario, filterApplication, searchTextModified });
        // Strip off 'relevance' sort field
        request.sortOptions = stripOffRelevance(request.sortOptions, Constants.SortActionIds.Relevance);
        this.sources
            .searchSource
            .getQueryResults(request, ({ responseWithActivityId, source }) => {
                if (responseWithActivityId) {
                    const workItemSearchResponse = responseWithActivityId.response as WorkItemSearchResponse;
                    const activityId = responseWithActivityId.activityId && responseWithActivityId.activityId[0];
                    let activeItem = undefined;

                    if (fetchMoreScenario) {
                        activeItem = this.getAggregatedState().selectedItem;
                    }

                    this.handleResultsLoaded(request, workItemSearchResponse, source, activityId, activeItem);

                    this.timerManagement.setTimeout(() => {
                        if (!this.includeFacets) {
                            this.fetchFacets(request);
                        }
                    }, 0);
                }
            }, (error: ISearchFailedPayload<WorkItemSearchRequest>) => {
                this.actionsHub.searchFailed.invoke({ ...error, query: request });
            });
    }

    private getInitialTake = (): number => {
        const infiniteScroll = isFeatureFlagEnabled(this.pageContext, Constants.FeatureFlagConstants.InfiniteScroll, false);
        return infiniteScroll ? Constants.WorkItemSearchInfiniteScrollTakeResults : Constants.WorkItemSearchTakeResults;
    }

    private comparer = (sortOption: EntitySortOption): (f: WorkItemResult, s: WorkItemResult) => number => {
        const sortFieldType = Constants.FieldType[sortOption.field];

        let compareDelegate: (f: string, s: string) => number;
        if (sortFieldType === WorkItemFieldType.DateTime) {
            compareDelegate = (first: string, second: string) => {
                const d1 = new Date(first);
                const d2 = new Date(second);
                return d1.getTime() - d2.getTime();
            }
        } else if (sortFieldType === WorkItemFieldType.Integer) {
            compareDelegate = (first: string, second: string) => {
                const v1 = parseInt(first);
                const v2 = parseInt(second);

                return v1 - v2;
            }
        } else {
            // Changing it from ignoreCaseComparer to localeIgnoreCaseComparer for localisation.
            compareDelegate = localeIgnoreCaseComparer;
        }

        return (
            (refName: string, order: string, comparisonDelegate: (f: string, s: string) => number) => {
                return (first: WorkItemResult, second: WorkItemResult) => {
                    const v1: string = getFieldValue(first.fields, refName);
                    const v2: string = getFieldValue(second.fields, refName);

                    let compareValue: number = comparisonDelegate(v1, v2);

                    // if field to sort on have same values in both rows, then break the tie based on the work item id.
                    if (compareValue === 0) {
                        const id1 = parseInt(getFieldValue(first.fields, Constants.SortActionIds.ID));
                        const id2 = parseInt(getFieldValue(second.fields, Constants.SortActionIds.ID));

                        compareValue = id1 - id2;
                    }

                    if (order === "desc") {
                        return -compareValue;
                    }

                    return compareValue;
                };
            })(sortOption.field, sortOption.sortOrder, compareDelegate);
    }

    /**
    * In the very corner case where the search request (fired by SeedDataSearchService) completes
    * after the stores are initialized but before the search page is initialized (loadInitialState),
    * we need to initialize the search page with the query.
    */
    private initializeSearchPage = (query: WorkItemSearchRequest | undefined, responseQuery: WorkItemSearchRequest): void => {
        if (!query) {
            this.actionsHub.pageInitializationStarted.invoke({ request: responseQuery });
        }
    }
}